import { Controller } from "@hotwired/stimulus"

// The "Add meal" sheet for a day — the day + meal type are picked via a
// native <select> dropdown right on the day row (OS picker wheel on
// mobile), so this controller opens straight to choosing a recipe or typing
// a quick meal. Sits on the same element as `modal` so it can drive
// open/close directly, matching week_navigation_controller's own pattern of
// reaching a sibling controller via getControllerForElementAndIdentifier.
export default class extends Controller {
  static targets = ["choiceStep", "typeStep", "groceryStep", "dateField", "mealTypeField", "nameInput", "chipsFrame"]
  static values = { date: String, mealType: String }

  #awaitingGroceryDecision = false

  chooseFromDropdown(event) {
    const mealType = event.target.value
    if (!mealType) return
    event.target.value = "" // reset to the placeholder — this is a momentary trigger, not a stored choice

    this.dateValue = event.params.date
    this.mealTypeValue = mealType
    this.dateFieldTarget.value = this.dateValue
    this.mealTypeFieldTarget.value = this.mealTypeValue
    this.nameInputTarget.value = ""
    this.#showChoice()
    this.#modal?.openFresh()
  }

  chooseType() {
    this.chipsFrameTarget.src = `/remembered_meals?meal_type=${this.mealTypeValue}`
    this.#showType()
  }

  chooseRecipe() {
    const weekNavEl = document.querySelector('[data-controller~="week-navigation"]')
    const weekNav = weekNavEl && this.application.getControllerForElementAndIdentifier(weekNavEl, "week-navigation")
    this.#modal?.close()
    weekNav?.openPicker({ params: { date: this.dateValue, mealType: this.mealTypeValue } })
  }

  selectRemembered(event) {
    this.nameInputTarget.value = event.params.name
    this.nameInputTarget.focus()
  }

  // From the type-a-meal step, back returns to the recipe/type choice,
  // keeping the meal type chosen from the dropdown.
  backToChoice() {
    this.#showChoice()
  }

  // The type-a-meal form's turbo_stream response always refreshes
  // groceryStep — with a filled confirm (name matched something
  // remembered) or an empty placeholder (brand-new meal, nothing to
  // pre-fill). But Turbo doesn't guarantee that swap has landed in the DOM
  // by the time turbo:submit-end fires — in the real browser it can arrive
  // either side of that event. So we flag "a decision is owed" the moment
  // the form starts submitting, and let groceryStepTargetConnected (fired
  // by Stimulus whenever that swap actually happens, whichever order it
  // arrives in) settle it: confirm groceries, or just close since there's
  // nothing to add yet.
  submitStartedFromType() {
    this.#awaitingGroceryDecision = true
  }

  submitEndedFromType(event) {
    if (!event.detail.success) this.#awaitingGroceryDecision = false
  }

  groceryStepTargetConnected(element) {
    if (!this.#awaitingGroceryDecision) return
    this.#awaitingGroceryDecision = false

    if (element.querySelector("form")) {
      this.#showGrocery()
    } else {
      this.#modal?.close()
    }
  }

  // Bound to turbo:submit-end on the one-tap grocery-confirm form — that
  // response only updates the day's entry row, not this modal, so it has to
  // close itself once the groceries are actually added.
  submitEndedFromGrocery(event) {
    if (event.detail.success) this.#modal?.close()
  }

  get #modal() {
    const sheet = document.getElementById("quick-meal-modal")
    return sheet && this.application.getControllerForElementAndIdentifier(sheet, "modal")
  }

  #showChoice() {
    this.choiceStepTarget.classList.remove("hidden")
    this.typeStepTarget.classList.add("hidden")
    this.groceryStepTarget.classList.add("hidden")
  }

  #showType() {
    this.choiceStepTarget.classList.add("hidden")
    this.typeStepTarget.classList.remove("hidden")
    this.groceryStepTarget.classList.add("hidden")
  }

  #showGrocery() {
    this.choiceStepTarget.classList.add("hidden")
    this.typeStepTarget.classList.add("hidden")
    this.groceryStepTarget.classList.remove("hidden")
  }
}
